/**
 * Platform Service
 * Provides platform detection for web, Android, and iOS
 * Used to select optimal geolocation strategies per platform
 */

import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';

export type Platform = 'ios' | 'android' | 'web';

export interface PlatformInfo {
  platform: Platform;
  isNative: boolean;
  hasGeolocationPlugin: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PlatformService {
  private readonly platformInfo: PlatformInfo;

  constructor() {
    this.platformInfo = this.detectPlatform();
    console.log(`📱 Platform detected: ${this.platformInfo.platform} (native: ${this.platformInfo.isNative})`);
  }

  /**
   * Detect current platform using Capacitor
   */
  private detectPlatform(): PlatformInfo {
    try {
      const platform = Capacitor.getPlatform() as Platform;
      const isNative = Capacitor.isNativePlatform();
      const hasGeolocationPlugin = Capacitor.isPluginAvailable('Geolocation');

      return {
        platform,
        isNative,
        hasGeolocationPlugin,
      };
    } catch {
      // Fallback if Capacitor is not available
      return {
        platform: 'web',
        isNative: false,
        hasGeolocationPlugin: false,
      };
    }
  }

  /**
   * Get current platform ('ios', 'android', or 'web')
   */
  getPlatform(): Platform {
    return this.platformInfo.platform;
  }

  /**
   * Check if running as native app (iOS or Android)
   */
  isNative(): boolean {
    return this.platformInfo.isNative;
  }

  /**
   * Check if native geolocation plugin is available
   */
  hasNativeGeolocation(): boolean {
    return this.platformInfo.isNative && this.platformInfo.hasGeolocationPlugin;
  }

  /**
   * Check if running on iOS
   */
  isIOS(): boolean {
    return this.platformInfo.platform === 'ios';
  }

  /**
   * Check if running on Android
   */
  isAndroid(): boolean {
    return this.platformInfo.platform === 'android';
  }

  /**
   * Check if running on web browser
   */
  isWeb(): boolean {
    return this.platformInfo.platform === 'web';
  }

  /**
   * Check if running on a mobile device (native app or mobile web browser)
   * Used for UI/UX adaptations like larger preload distances for touch scrolling
   */
  isMobile(): boolean {
    // Native apps are always mobile
    if (this.platformInfo.isNative) {
      return true;
    }

    // For web, detect mobile browser
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent;
      const mobileRegex = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i;
      const isMobileUA = mobileRegex.test(userAgent);
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;

      return isMobileUA || (isTouchDevice && isSmallScreen);
    }

    return false;
  }

  /**
   * Get full platform info
   */
  getInfo(): PlatformInfo {
    return { ...this.platformInfo };
  }
}
